import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';

import {
  ONBOARDING_MULTI_QUESTIONS,
  ONBOARDING_SINGLE_QUESTIONS,
  ProducerOnboardingV1
} from '@core/models/producer-onboarding';

interface SingleRow {
  question: string;
  answer: string | null;
}

interface MultiRow {
  question: string;
  answers: string[];
}

@Component({
  selector: 'app-producer-onboarding-review',
  standalone: true,
  imports: [CommonModule, CardModule, MessageModule, SkeletonModule, TagModule],
  templateUrl: './producer-onboarding-review.html',
  styleUrl: './producer-onboarding-review.scss'
})
export class ProducerOnboardingReview {
  readonly onboarding = input<ProducerOnboardingV1 | null>(null);
  readonly loading = input<boolean>(false);
  readonly error = input<boolean>(false);

  readonly singleRows = computed<SingleRow[]>(() => {
    const onboarding = this.onboarding();
    if (!onboarding) {
      return [];
    }
    return ONBOARDING_SINGLE_QUESTIONS.map((q) => {
      const value = onboarding[q.key] as string | null;
      return { question: q.question, answer: value ? (q.labels[value] ?? value) : null };
    });
  });

  readonly multiRows = computed<MultiRow[]>(() => {
    const onboarding = this.onboarding();
    if (!onboarding) {
      return [];
    }
    return ONBOARDING_MULTI_QUESTIONS.map((q) => {
      const values = (onboarding[q.key] as string[] | undefined) ?? [];
      return { question: q.question, answers: values.map((v) => q.labels[v] ?? v) };
    });
  });

  readonly termsLine = computed<string | null>(() => {
    const onboarding = this.onboarding();
    if (!onboarding?.termsAcceptedOn) {
      return null;
    }
    const date = new Date(onboarding.termsAcceptedOn).toLocaleDateString();
    return onboarding.termsVersion ? `Accepted ${date} (v${onboarding.termsVersion})` : `Accepted ${date}`;
  });

  readonly isEmpty = computed<boolean>(() => {
    const onboarding = this.onboarding();
    if (!onboarding) {
      return false;
    }
    return (
      this.singleRows().every((r) => !r.answer) &&
      this.multiRows().every((r) => r.answers.length === 0) &&
      !onboarding.termsAcceptedOn
    );
  });
}
